import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Collapse, Divider, FormControl,
  FormControlLabel, FormLabel, MenuItem, Paper, Radio, RadioGroup,
  Stack, Switch, TextField, Typography
} from '@mui/material';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import api from '../../../services/api';

const ORG_TYPES = ['Company', 'Non-profit', 'Educational institution', 'Government', 'Community group', 'Other'];

const CorporateSection = () => {
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState(''); // 'join' | 'create'

  // Join state
  const [joinCode, setJoinCode] = useState('');
  const [joinRole, setJoinRole] = useState('viewer');
  const [joinMsg, setJoinMsg] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinResult, setJoinResult] = useState(null);

  // Create state
  const [createForm, setCreateForm] = useState({
    org_name: '', org_type: '', website: '', motivation: '',
    requestor_name: '', requestor_role: '', requestor_phone: '', requestor_email: '',
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createResult, setCreateResult] = useState(null);

  const handleJoinSubmit = async () => {
    if (!joinCode.trim()) return;
    setJoinLoading(true);
    setJoinResult(null);
    try {
      const data = await api.submitOrgJoin({
        join_code: joinCode.trim().toUpperCase(),
        requested_role: joinRole,
        message: joinMsg.trim() || undefined,
      });
      setJoinResult({ success: true, message: data.message, org_name: data.org_name });
    } catch (err) {
      setJoinResult({ success: false, message: err.message || 'Failed to submit request' });
    } finally {
      setJoinLoading(false);
    }
  };

  const handleCreateChange = (field) => (e) =>
    setCreateForm(f => ({ ...f, [field]: e.target.value }));

  const handleCreateSubmit = async () => {
    const required = ['org_name', 'requestor_name', 'requestor_email'];
    if (required.some(f => !createForm[f].trim())) return;
    setCreateLoading(true);
    setCreateResult(null);
    try {
      const data = await api.submitOrgCreation(createForm);
      setCreateResult({ success: true, join_code: data.join_code, org_id: data.org_id });
    } catch (err) {
      setCreateResult({ success: false, message: err.message || 'Failed to submit request' });
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <Box>
      {/* Toggle */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={e => { setEnabled(e.target.checked); setMode(''); setJoinResult(null); setCreateResult(null); }}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1" fontWeight={600}>Enable Organisation Account</Typography>
              <Typography variant="caption" color="text.secondary">
                Join an existing organisation or create a new one for your team.
              </Typography>
            </Box>
          }
          labelPlacement="end"
        />
      </Paper>

      <Collapse in={enabled}>
        {/* Mode picker */}
        {!mode && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
            <Paper
              variant="outlined"
              onClick={() => setMode('join')}
              sx={{ p: 3, flex: 1, borderRadius: 2, cursor: 'pointer', textAlign: 'center',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' } }}
            >
              <GroupAddIcon sx={{ fontSize: 36, color: 'primary.main', mb: 1 }} />
              <Typography variant="subtitle1" fontWeight={700}>Join an Organisation</Typography>
              <Typography variant="body2" color="text.secondary">
                Use a join code provided by your organisation admin.
              </Typography>
            </Paper>
            <Paper
              variant="outlined"
              onClick={() => setMode('create')}
              sx={{ p: 3, flex: 1, borderRadius: 2, cursor: 'pointer', textAlign: 'center',
                    '&:hover': { borderColor: 'secondary.main', bgcolor: 'secondary.50' } }}
            >
              <AddBusinessIcon sx={{ fontSize: 36, color: 'secondary.main', mb: 1 }} />
              <Typography variant="subtitle1" fontWeight={700}>Create an Organisation</Typography>
              <Typography variant="body2" color="text.secondary">
                Submit a request to create a new organisation account.
              </Typography>
            </Paper>
          </Stack>
        )}

        {/* ── JOIN FLOW ── */}
        {mode === 'join' && (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <GroupAddIcon color="primary" />
              <Typography variant="h6" fontWeight={700}>Join an Organisation</Typography>
              <Button size="small" sx={{ ml: 'auto' }} onClick={() => setMode('')}>Back</Button>
            </Stack>

            {joinResult?.success ? (
              <Alert severity="success">
                <Typography variant="body2" fontWeight={600}>Request submitted!</Typography>
                <Typography variant="body2">
                  Your request to join <strong>{joinResult.org_name}</strong> has been sent to the admin.
                  You'll receive an email once it's reviewed.
                </Typography>
              </Alert>
            ) : (
              <Stack spacing={2}>
                <TextField
                  label="Organisation Join Code"
                  value={joinCode}
                  onChange={e => setJoinCode(e.target.value.toUpperCase())}
                  inputProps={{ maxLength: 8, style: { letterSpacing: 4, fontFamily: 'monospace', fontWeight: 700 } }}
                  helperText="8-character code shared by your organisation admin"
                  required
                />
                <FormControl>
                  <FormLabel>Requested role</FormLabel>
                  <RadioGroup row value={joinRole} onChange={e => setJoinRole(e.target.value)}>
                    <FormControlLabel value="organiser" control={<Radio />} label="Organiser" />
                    <FormControlLabel value="viewer" control={<Radio />} label="Viewer" />
                  </RadioGroup>
                </FormControl>
                <TextField
                  label="Message (optional)"
                  value={joinMsg}
                  onChange={e => setJoinMsg(e.target.value)}
                  multiline rows={2}
                  placeholder="Tell the admin why you want to join…"
                />
                {joinResult && !joinResult.success && (
                  <Alert severity="error">{joinResult.message}</Alert>
                )}
                <Button
                  variant="contained"
                  onClick={handleJoinSubmit}
                  disabled={joinLoading || !joinCode.trim()}
                  startIcon={joinLoading ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  Submit Request
                </Button>
              </Stack>
            )}
          </Paper>
        )}

        {/* ── CREATE FLOW ── */}
        {mode === 'create' && (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <AddBusinessIcon color="secondary" />
              <Typography variant="h6" fontWeight={700}>Create an Organisation</Typography>
              <Button size="small" sx={{ ml: 'auto' }} onClick={() => setMode('')}>Back</Button>
            </Stack>

            {createResult?.success ? (
              <Stack spacing={2}>
                <Alert severity="success">
                  <Typography variant="body2" fontWeight={600}>Organisation created!</Typography>
                  <Typography variant="body2">
                    Your organisation has been set up and you've been added as Admin.
                    A confirmation email has been sent. Switch to your org from the account icon in the top right.
                  </Typography>
                </Alert>
                {createResult.join_code && (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                      Organisation Join Code
                    </Typography>
                    <Typography variant="h4" fontFamily="monospace" fontWeight={800} letterSpacing={6} color="primary.main">
                      {createResult.join_code}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Share this with colleagues so they can request to join.
                    </Typography>
                  </Paper>
                )}
              </Stack>
            ) : (
              <Stack spacing={3}>
                <Alert severity="info" sx={{ fontSize: 13 }}>
                  Use your official work email for faster approval. Organisation details are
                  independent from your personal account — this ensures continuity even if you leave.
                </Alert>

                {/* Organisation details */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>Organisation Details</Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Organisation Name" required
                      value={createForm.org_name} onChange={handleCreateChange('org_name')}
                    />
                    <TextField
                      select label="Organisation Type"
                      value={createForm.org_type} onChange={handleCreateChange('org_type')}
                    >
                      <MenuItem value=""><em>Select type</em></MenuItem>
                      {ORG_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                    <TextField
                      label="Website (optional)"
                      value={createForm.website} onChange={handleCreateChange('website')}
                      placeholder="https://your-organisation.com"
                    />
                    <TextField
                      label="Why do you need an organisation account?"
                      value={createForm.motivation} onChange={handleCreateChange('motivation')}
                      multiline rows={3}
                      placeholder="Describe your use case…"
                    />
                  </Stack>
                </Box>

                <Divider />

                {/* Requestor details */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>Your Details</Typography>
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        label="Your Full Name" required fullWidth
                        value={createForm.requestor_name} onChange={handleCreateChange('requestor_name')}
                      />
                      <TextField
                        label="Your Role / Job Title" fullWidth
                        value={createForm.requestor_role} onChange={handleCreateChange('requestor_role')}
                        placeholder="e.g. Marketing Manager"
                      />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        label="Official Email" required fullWidth type="email"
                        value={createForm.requestor_email} onChange={handleCreateChange('requestor_email')}
                        helperText="Use your work email for faster approval"
                      />
                      <TextField
                        label="Phone Number" fullWidth
                        value={createForm.requestor_phone} onChange={handleCreateChange('requestor_phone')}
                      />
                    </Stack>
                  </Stack>
                </Box>

                {createResult && !createResult.success && (
                  <Alert severity="error">{createResult.message}</Alert>
                )}

                <Alert severity="warning" sx={{ fontSize: 13 }}>
                  Once submitted, we'll review your request and get back to you within 5 business days.
                  A confirmation email will be sent immediately.
                </Alert>

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleCreateSubmit}
                  disabled={
                    createLoading ||
                    !createForm.org_name.trim() ||
                    !createForm.requestor_name.trim() ||
                    !createForm.requestor_email.trim()
                  }
                  startIcon={createLoading ? <CircularProgress size={16} color="inherit" /> : <BusinessCenterIcon />}
                >
                  Submit Organisation Request
                </Button>
              </Stack>
            )}
          </Paper>
        )}
      </Collapse>
    </Box>
  );
};

export default CorporateSection;
